// InspectionHistoryScreen.js
// Matches the desktop MVIRS inspection history screen 1:1
// Grid with columns: Insp No, Work Order, Sticker, Registrant, VIN, Date, Plate, Fleet No, Type, Status
// Type radio: AI, SI, AO, IM
// Status radio: Complete, Held, Voided, Failed, Temp
// Search: field dropdown + starts-with text input
// Pagination: Page X of Y

/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const TYPES = [
  { key: 1, label: 'AI' },
  { key: 2, label: 'SI' },
  { key: 3, label: 'AO' },
  { key: 4, label: 'IM' },
];

const STATUSES = [
  { key: 'C', label: 'Complete' },
  { key: 'H', label: 'Held' },
  { key: 'V', label: 'Voided' },
  { key: 'F', label: 'Failed' },
  { key: 'T', label: 'Temp' },
];

const SEARCH_FIELDS = [
  { key: 'Registrant', label: 'Registrant' },
  { key: 'Plate',      label: 'Plate' },
  { key: 'VIN',        label: 'VIN' },
  { key: 'Sticker',    label: 'Sticker' },
  { key: 'WorkOrder',  label: 'Work Order' },
];

const PAGE_SIZE = 25;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const typeLabel = (fkType) => {
  switch (fkType) {
    case 1: return 'AI';
    case 2: return 'SI';
    case 3: return 'AO';
    case 4: return 'IM';
    default: return '';
  }
};

const statusLabel = (status) => {
  switch (status) {
    case 'C': return 'Complete';
    case 'H': return 'Held';
    case 'V': return 'Voided';
    case 'F': return 'Failed';
    case 'T': return 'Temp';
    default: return status;
  }
};

export default function InspectionHistoryScreen() {
  const { user, logout, getClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Restore state if returning from inspection detail
  const restored = location.state?.historyState;

  const [inspType, setInspType]         = useState(restored?.inspType || 1);
  const [status, setStatus]             = useState(restored?.status || 'C');
  const [searchField, setSearchField]   = useState(restored?.searchField || 'Registrant');
  const [searchText, setSearchText]     = useState(restored?.searchText || '');
  const [page, setPage]                 = useState(restored?.page || 1);
  const [totalPages, setTotalPages]     = useState(1);
  const [total, setTotal]               = useState(0);
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const load = useCallback(async (type, stat, field, text, pg) => {
    setLoading(true);
    setError('');
    try {
      const client = getClient();
      const params = new URLSearchParams({
        type,
        status: stat,
        page: pg,
        pageSize: PAGE_SIZE,
        searchField: field,
        search: text,
      });
      const res = await client.get(`/api/inspection/history?${params}`);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch {
      setError('Could not load inspections.');
    } finally {
      setLoading(false);
    }
  }, [getClient]);

  useEffect(() => {
    load(inspType, status, searchField, searchText, page);
  }, [inspType, status, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(inspType, status, searchField, searchText, 1);
  };

  const handleTypeChange = (t) => { setInspType(t); setPage(1); setSearchText(''); };
  const handleStatusChange = (s) => { setStatus(s); setPage(1); setSearchText(''); };

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleRowClick = (item) => {
    navigate(`/inspection/${item.pkInspection}`, {
      state: {
        pkInspection: item.pkInspection,
        stickerNumber: item.stickerNumber || null,
        isReadOnly: item.status !== 'H',
        // Pass current history screen state so Close can restore it
        historyState: { inspType, status, searchField, searchText, page },
      }
    });
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.headerTitle}>MVIRS</span>
          <span style={s.headerSub}>Inspection History — {user?.shopName}</span>
        </div>
        <div style={s.headerRight}>
          {/* Search bar */}
          <form onSubmit={handleSearch} style={s.searchForm}>
            <span style={s.findLabel}>Find</span>
            <select style={s.searchFieldSelect} value={searchField} onChange={e => setSearchField(e.target.value)}>
              {SEARCH_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <span style={s.findLabel}>starting with</span>
            <input
              style={s.searchInput}
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder=""
            />
            <button type="submit" style={s.findBtn}>Find</button>
            <button type="button" style={s.clearBtn} onClick={() => { setSearchText(''); setPage(1); load(inspType, status, searchField, '', 1); }}>✕</button>
          </form>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      {/* Grid */}
      <div style={s.gridWrap}>
        {error && <div style={s.error}>{error}</div>}

        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>Insp No</th>
              <th style={s.th}>Work Order</th>
              <th style={{ ...s.th, minWidth: 130 }}>Sticker</th>
              <th style={{ ...s.th, minWidth: 200 }}>Registrant</th>
              <th style={{ ...s.th, minWidth: 160 }}>VIN</th>
              <th style={s.th}>Date</th>
              <th style={s.th}>Plate</th>
              <th style={s.th}>Fleet No</th>
              <th style={s.th}>Type</th>
              <th style={s.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={s.centerCell}><div style={s.spinner} /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} style={s.centerCell}>No records found.</td></tr>
            ) : rows.map((row, idx) => (
              <tr
                key={row.pkInspection}
                style={{ ...s.tr, backgroundColor: idx % 2 === 0 ? '#fff' : '#f7f7f7' }}
                onClick={() => handleRowClick(row)}
              >
                <td style={s.td}>{row.inspectionNumber || ''}</td>
                <td style={s.td}>{row.workOrder}</td>
                <td style={s.td}>{row.stickerNumber}</td>
                <td style={s.td}>{row.registrantName}</td>
                <td style={s.td}>{row.vin}</td>
                <td style={s.td}>{formatDate(row.inspectionDate)}</td>
                <td style={s.td}>{row.licensePlate}</td>
                <td style={s.td}>{row.fleetNumber}</td>
                <td style={s.td}>{typeLabel(row.fkInspectionType)}</td>
                <td style={s.td}>{statusLabel(row.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={s.footer}>
        {/* Type radio buttons */}
        <div style={s.footerSection}>
          <span style={s.footerLabel}>Type:</span>
          {TYPES.map(t => (
            <label key={t.key} style={s.radioLabel}>
              <input
                type="radio"
                name="type"
                checked={inspType === t.key}
                onChange={() => handleTypeChange(t.key)}
                style={s.radio}
              />
              {t.label}
            </label>
          ))}
        </div>

        {/* Status radio buttons */}
        <div style={s.footerSection}>
          <span style={s.footerLabel}>Status:</span>
          {STATUSES.map(st => (
            <label key={st.key} style={s.radioLabel}>
              <input
                type="radio"
                name="status"
                checked={status === st.key}
                onChange={() => handleStatusChange(st.key)}
                style={s.radio}
              />
              {st.label}
            </label>
          ))}
        </div>

        {/* Pagination */}
        <div style={s.footerSection}>
          <button
            style={s.pageBtn}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
          >◀</button>
          <span style={s.pageInfo}>Page {page} of {totalPages} &nbsp;({total} records)</span>
          <button
            style={s.pageBtn}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >▶</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:             { minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f0f0f0', fontFamily: 'system-ui, sans-serif', fontSize: 13 },
  header:           { backgroundColor: '#1a1a2e', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  headerLeft:       { display: 'flex', flexDirection: 'column' },
  headerTitle:      { color: '#fff', fontSize: 18, fontWeight: 700 },
  headerSub:        { color: 'rgba(255,255,255,0.7)', fontSize: 12 },
  headerRight:      { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  searchForm:       { display: 'flex', alignItems: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6 },
  findLabel:        { color: '#fff', fontSize: 13 },
  searchFieldSelect:{ padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 },
  searchInput:      { padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, width: 160 },
  findBtn:          { padding: '3px 10px', backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  clearBtn:         { padding: '3px 8px', backgroundColor: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  logoutBtn:        { padding: '4px 12px', backgroundColor: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  gridWrap:         { flex: 1, overflow: 'auto' },
  table:            { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' },
  thead:            { backgroundColor: '#d0d8e8', position: 'sticky', top: 0 },
  th:               { padding: '6px 8px', textAlign: 'left', fontWeight: 700, fontSize: 13, borderBottom: '2px solid #aab', borderRight: '1px solid #ccd', whiteSpace: 'nowrap' },
  tr:               { cursor: 'pointer' },
  td:               { padding: '4px 8px', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #eee', whiteSpace: 'nowrap', fontSize: 13 },
  centerCell:       { textAlign: 'center', padding: 40, color: '#666' },
  spinner:          { width: 32, height: 32, border: '4px solid #e0e0e0', borderTop: '4px solid #007AFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  error:            { padding: 12, backgroundColor: '#ffe0e0', color: '#cc0000', fontSize: 14 },
  footer:           { backgroundColor: '#e8e8e8', borderTop: '2px solid #bbb', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' },
  footerSection:    { display: 'flex', alignItems: 'center', gap: 12 },
  footerLabel:      { fontWeight: 700, fontSize: 13, marginRight: 4 },
  radioLabel:       { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' },
  radio:            { cursor: 'pointer' },
  pageBtn:          { padding: '2px 10px', fontSize: 14, cursor: 'pointer', backgroundColor: '#fff', border: '1px solid #bbb', borderRadius: 4 },
  pageInfo:         { fontSize: 13, color: '#333' },
};